import { ENDPOINTS } from 'api/endpoints';
import { useGetQuery } from 'api/useGetQuery';
import PoweredByHeader from 'components/PoweredByHeader';
import { useDataEnvironment } from 'contexts/DataEnvironmentContext';
import React, { useState } from 'react';
import { showError } from 'utils/showError';
import { useMutation, useQueryClient } from 'react-query';

import {
  DataTypeProvider,
  DataTypeProviderProps,
  IntegratedFiltering,
  IntegratedSorting,
  SearchState,
  SortingState,
} from '@devexpress/dx-react-grid';
import { Column } from '@devexpress/dx-react-grid';

import {
  Grid,
  VirtualTable,
  TableHeaderRow,
  TableColumnResizing,
  Toolbar,
  SearchPanel,
} from '@devexpress/dx-react-grid-bootstrap4';

import Tabs from '../../components/Tabs';
import { DateTypeProvider } from 'components/DataGrid/DataTypeProviders/DateTypeProvider';
import { axiosClient } from 'api/axiosClient';
import { showSuccess } from 'utils/showSuccess';
import { MappedAsset } from 'types/MappedAsset';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { errorMessage } from 'utils/errorMessage';
import { Info } from 'components/Icons/Info';

const MappedAssets: React.FC = () => {
  const queryClient = useQueryClient();
  const [dataEnvironment] = useDataEnvironment();
  const [selectedAsset, setSelectedAsset] = useState<MappedAsset>();
  const [showUnmapConfirmation, setShowUnmapConfirmation] = useState<boolean>(false);
  const [sortingStateColumnExtensions] = useState([
    { columnName: 'actions', sortingEnabled: false },
  ]);

  const columns: Array<Column> = [
    { name: 'resourceName', title: 'Asset' },
    { name: 'userEnteredProductName', title: 'Product Name' },
    { name: 'userEnteredFirmName', title: 'Firm Name' },
    { name: 'lastMappedOn', title: 'Date Mapped' },
    { name: 'userEnteredProductId', title: 'Product ID' },
    { name: 'lastMappedByUserEmail', title: 'Mapped By' },
    { name: 'actions', title: 'Actions' },
  ];

  const [defaultColumnWidths] = useState(
    columns.map((column) => ({
      columnName: column.name,
      width: (window.innerWidth - 50) / columns.length,
    })),
  );

  const unmap = useMutation(
    (id: string) => axiosClient.post(ENDPOINTS.unmapAsset(dataEnvironment!.id, id)),
    {
      onError: (error: any) => {
        showError(
          errorMessage({
            error,
            defaultMessage: 'An error occurred when attempting to unmap the asset.',
          }),
        );
      },
      onSuccess: () => {
        showSuccess('Successfully unmapped asset.');
        queryClient.invalidateQueries('mappedAssets');
      },
    },
  );

  const { isFetching, data } = useGetQuery<MappedAsset[]>(
    ENDPOINTS.mappedAssets(dataEnvironment!.id),
    {
      queryKey: 'mappedAssets',
      onError: (error: any) => {
        showError(
          errorMessage({ error, defaultMessage: 'An error occurred retrieving mapped assets.' }),
        );
      },
    },
  );

  const ActionsFormatter: React.FC<DataTypeProvider.ValueFormatterProps> = ({
    row: data,
  }: DataTypeProvider.ValueFormatterProps) => {
    return (
      <button
        className="btn btn-link p-0"
        onClick={() => {
          setSelectedAsset(data);
          setShowUnmapConfirmation(true);
        }}>
        Unmap
      </button>
    );
  };

  const ActionsTypeProvider: React.FC<DataTypeProviderProps> = (props: DataTypeProviderProps) => (
    <DataTypeProvider formatterComponent={ActionsFormatter} {...props} />
  );

  const ResourceNameTypeFormatter: React.FC<DataTypeProvider.ValueFormatterProps> = ({
    value, row
  }: DataTypeProvider.ValueFormatterProps) => {
    let displayValue = value;
    if (!displayValue) {
      displayValue = <span><Info className="text-danger" /> {`Resource ${row.resourceId} was deleted`}</span>;
    }
    return displayValue;
  };

  return (
    <>
      <PoweredByHeader />
      <Tabs />

      <ConfirmModal
        show={showUnmapConfirmation}
        onClose={() => {
          setShowUnmapConfirmation(false);
        }}
        onConfirm={() => {
          setShowUnmapConfirmation(false);
          unmap.mutate(selectedAsset!.productUploadAssetId);
        }}
        title="Unmap Asset"
        message="Are you sure? Unmapping this asset could invalidate one or more Product Uploads."
      />

      {isFetching && <LoadingIndicator />}

      {!isFetching && (
        <Grid rows={data || []} columns={columns}>
          <DataTypeProvider formatterComponent={ResourceNameTypeFormatter} for={['resourceName']} />
          <DateTypeProvider for={['lastMappedOn']} />
          <ActionsTypeProvider for={['actions']} />

          <SortingState
            columnExtensions={sortingStateColumnExtensions}
            defaultSorting={[{ columnName: 'resourceName', direction: 'asc' }]}
          />
          <IntegratedSorting />
          <SearchState />
          <IntegratedFiltering />

          <VirtualTable columnExtensions={[{ columnName: 'actions' }]} />
          <TableColumnResizing defaultColumnWidths={defaultColumnWidths} />
          <TableHeaderRow showSortingControls />
          <Toolbar />
          <SearchPanel />
        </Grid>
      )}
    </>
  );
};

export default MappedAssets;
